import type { ApiEnvelope } from "../types";
import { clearSession, persistSession, readStoredUser } from "./session-storage";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000/api";

export function apiAssetUrl(path?: string | null) {
  if (!path) return "";
  if (/^(?:https?:\/\/|data:image\/)/i.test(path)) return path;
  return `${API_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

type RefreshOutcome = "refreshed" | "invalid" | "unavailable" | "superseded";
let refreshPromise: Promise<RefreshOutcome> | null = null;

async function refreshSession(expectedToken: string): Promise<RefreshOutcome> {
  if (!refreshPromise)
    refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_URL}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
        });
        if (response.status === 401) return "invalid";
        if (!response.ok) return "unavailable";
        const payload = (await response.json()) as ApiEnvelope<{
          token: string;
          user: import("../types").User;
        }>;
        if (localStorage.getItem("kidix_token") !== expectedToken || payload.data.user.id !== readStoredUser(localStorage)?.id) return "superseded";
        persistSession(
          localStorage,
          payload.data,
        );
        return "refreshed";
      } catch {
        return "unavailable";
      } finally {
        refreshPromise = null;
      }
    })();
  return refreshPromise;
}

function isAnonymousPublicRead(path: string, options: RequestInit) {
  const method = (options.method ?? "GET").toUpperCase();
  return (
    method === "GET" &&
    (path === "/public/landing" || path === "/public/instructors" || path.startsWith("/public/instructors/") || path.startsWith("/public/courses/") || path.startsWith("/public/certificates/"))
  );
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
  retry = true,
): Promise<T> {
  const token = localStorage.getItem("kidix_token");
  const publicRead = isAnonymousPublicRead(path, options);
  const accountId = readStoredUser(localStorage)?.id ?? null;
  const accountBound = !publicRead && (path === "/auth/me" || !path.startsWith("/auth/"));
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    cache: options.cache,
    credentials: publicRead ? "omit" : "include",
    headers: {
      ...(options.body || path === "/auth/logout" || path === "/auth/refresh" ? { "content-type": "application/json" } : { accept: "application/json" }),
      ...(token && !publicRead ? { authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const payload = (await response.json()) as ApiEnvelope<T>;
  if (accountBound && (readStoredUser(localStorage)?.id ?? null) !== accountId) throw new ApiClientError("The account changed while this request was loading.", 409, "ACCOUNT_CHANGED");
  const canRefresh = !publicRead && (path === "/auth/me" || !path.startsWith("/auth/"));
  if (response.status === 401 && token && retry && canRefresh) {
    const outcome = await refreshSession(token);
    if (outcome === "refreshed") return api<T>(path, options, false);
    if (outcome === "unavailable") throw new ApiClientError("The connection is temporarily unavailable. Retry without signing in again.", 503, "SESSION_TEMPORARILY_UNAVAILABLE");
    if (outcome === "superseded") throw new ApiClientError("The account changed while this request was loading.", 409, "ACCOUNT_CHANGED");
  }
  if (!response.ok) {
    if (response.status === 401 && canRefresh && token && localStorage.getItem("kidix_token") === token) {
      clearSession(localStorage);
      window.dispatchEvent(new Event("kidix:session-expired"));
    }
    throw new ApiClientError(
      payload.error ?? "Request failed",
      response.status,
      payload.code,
      payload.details,
    );
  }
  return payload.data;
}
